import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { Mail, Phone } from "lucide-react";

export default function Coaches() {
  const { site } = useSite();
  const coaches = site.coaches;
  const head = coaches.find((c) => c.is_head) || coaches[0];
  const assistants = coaches.filter((c) => c !== head);

  return (
    <div data-testid="coaches-page">
      <PageHeader
        eyebrow="Leadership"
        title="Coaching Staff"
        subtitle={site.content.subtitle_coaches}
        testid="coaches-header"
      />

      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {head && (
            <Reveal>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm mb-14" data-testid="coach-card-head">
                <div className="lg:col-span-2 h-80 lg:h-auto">
                  {head.photo ? (
                    <img src={head.photo} alt={`${head.name}, ${head.title}`} className="h-full w-full object-cover object-top" />
                  ) : (
                    <div className="h-full w-full bg-[#0F4C2A] flex items-center justify-center">
                      <span className="font-condensed text-6xl font-black text-[#F59E0B]">{head.name.split(" ").map((n) => n[0]).join("")}</span>
                    </div>
                  )}
                </div>
                <div className="lg:col-span-3 p-7 sm:p-10 flex flex-col justify-center">
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D97706]">{head.title}</span>
                  <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[#0F4C2A] mt-2">{head.name}</h2>
                  <p className="mt-4 text-slate-600 leading-relaxed text-sm sm:text-base">{head.bio}</p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    {head.email && (
                      <a href={`mailto:${head.email}`} data-testid="coach-email-link" className="inline-flex items-center gap-2 bg-[#0F4C2A] hover:bg-[#0A3823] text-white px-5 py-2.5 rounded-md text-sm font-bold uppercase tracking-wide transition-colors">
                        <Mail size={15} /> Email Coach
                      </a>
                    )}
                    {head.phone && (
                      <a href={`tel:${head.phone}`} data-testid="coach-phone-link" className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#0F4C2A]/50 text-slate-700 px-5 py-2.5 rounded-md text-sm font-bold uppercase tracking-wide transition-colors">
                        <Phone size={15} /> {head.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          <Reveal>
            <h2 className="font-condensed text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-[#0F4C2A] mb-8">Assistant Coaches</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {assistants.map((c, i) => (
              <Reveal key={c.id || c.name} delay={i * 0.08}>
                <div className="card-lift bg-white rounded-lg border border-slate-200 p-7 h-full hover:border-[#0F4C2A]/40" data-testid={`coach-card-${(c.name || "").toLowerCase().replace(/\s+/g, "-")}`}>
                  {c.photo ? (
                    <img src={c.photo} alt={c.name} className="h-16 w-16 rounded-full object-cover ring-2 ring-[#0F4C2A]/15 mb-5" />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-[#0F4C2A] flex items-center justify-center mb-5">
                      <span className="font-condensed text-xl font-bold text-[#F59E0B]">
                        {c.name.split(" ").map((n) => n[0]).join("")}
                      </span>
                    </div>
                  )}
                  <h3 className="font-condensed text-2xl font-bold uppercase tracking-tight text-slate-900">{c.name}</h3>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#D97706] mt-1">{c.title}</p>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{c.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
          {assistants.length === 0 && <p className="text-slate-500">Assistant coaches will be announced soon.</p>}
        </div>
      </section>
    </div>
  );
}
